import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsModules() {
  const [modules, setModules] = useState({
    sales_orders: false,
    delivery_notes: false,
    purchase_orders: false,
    stock_control: false,
    daily_planning: false,
    fleet_tracking: false,
    door_configurator: false,
    marketing_hub: true,
    marketing_image_generation: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();

      // API returns object, not array
      if (data && data.id) {
        setModules({
          sales_orders: data.module_sales_orders || false,
          delivery_notes: data.module_delivery_notes || false,
          purchase_orders: data.module_purchase_orders || false,
          stock_control: data.module_stock_control || false,
          daily_planning: data.module_daily_planning !== false, // Default true
          fleet_tracking: data.module_fleet_tracking !== false, // Default true
          door_configurator: data.module_door_configurator || false,
          marketing_hub: data.module_marketing_hub !== false,
          marketing_image_generation: data.module_marketing_image_generation || false
        });
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (moduleName) => {
    const newValue = !modules[moduleName];

    // Optimistic update
    setModules(prev => ({ ...prev, [moduleName]: newValue }));

    try {
      setSaving(true);

      // Get current settings ID
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();
      const settingsId = data.id || 1;

      // Update setting
      const updateRes = await apiFetch(`/api/company-settings/${settingsId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          [`module_${moduleName}`]: newValue
        })
      });

      if (!updateRes.ok) {
        throw new Error('Update failed');
      }

      // Wait a moment for database to commit, then reload
      await new Promise(resolve => setTimeout(resolve, 500));
      window.location.reload();
    } catch (err) {
      console.error('Error updating module:', err);
      // Revert on error
      setModules(prev => ({ ...prev, [moduleName]: !newValue }));
      alert('Failed to update module setting');
    } finally {
      setSaving(false);
    }
  };

  const moduleInfo = {
    sales_orders: {
      name: 'Sales Orders',
      description: 'Sell products without installation. Create sales orders, delivery notes, and invoices.',
      icon: '📦'
    },
    delivery_notes: {
      name: 'Delivery Notes',
      description: 'Track deliveries and proof of delivery. Generate delivery notes from sales orders.',
      icon: '🚚',
      requires: 'sales_orders'
    },
    purchase_orders: {
      name: 'Purchase Orders',
      description: 'Manage orders to suppliers. Track incoming stock and costs.',
      icon: '🛒'
    },
    stock_control: {
      name: 'Stock Control',
      description: 'Track inventory levels, stock movements, and warehouse locations.',
      icon: '📊'
    },
    daily_planning: {
      name: 'Daily Planning',
      description: 'Engineer scheduling, job boards, and route optimization.',
      icon: '📅'
    },
    fleet_tracking: {
      name: 'Fleet Tracking',
      description: 'Live vehicle GPS tracking, SafeTag integration, and vehicle-to-employee assignments.',
      icon: '🚗'
    },
    door_configurator: {
      name: 'Door Configurator',
      description: 'Parametric door designer with live SVG preview. Configure commercial doors and save as quotes.',
      icon: '🚪'
    },
    marketing_hub: {
      name: 'Marketing Hub',
      description: 'Campaigns, contacts, templates, topics, blogs, analytics and approval workflows.',
      icon: 'Marketing'
    },
    marketing_image_generation: {
      name: 'Generated Marketing Images',
      description: 'Allow tenants to generate campaign/blog images automatically.',
      icon: 'Images',
      requires: 'marketing_hub'
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading modules...</div>;
  }

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
        <h2 className="text-xl font-semibold">Optional Modules</h2>
        <p className="text-sm text-blue-100 mt-1">
          Enable or disable modules based on your business needs. Disabled modules are hidden from the interface.
        </p>
      </div>

      <div className="p-6 space-y-6">

      {/* Warning */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-blue-400">ℹ️</span>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Enabling or disabling a module will reload the page to update the navigation menu.
            </p>
          </div>
        </div>
      </div>

      {/* Modules List */}
      <div className="space-y-4">
        {Object.entries(moduleInfo).map(([key, info]) => {
          const isEnabled = modules[key];
          const isDisabled = info.requires && !modules[info.requires];

          return (
            <div
              key={key}
              className={`rounded-lg shadow p-6 border-2 transition ${
                isEnabled ? 'bg-green-50 border-green-300' : 'bg-blue-50 border-gray-300'
              } ${isDisabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{info.icon}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{info.name}</h3>
                      {info.requires && (
                        <p className="text-xs text-gray-500">
                          Requires: {moduleInfo[info.requires]?.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{info.description}</p>
                </div>

                <button
                  onClick={() => handleToggle(key)}
                  disabled={saving || isDisabled}
                  className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isEnabled ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isEnabled ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {isEnabled && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-xs text-green-700 font-medium">
                    ✓ Module enabled and visible in sidebar
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}

export default SettingsModules;
